import React from 'react';
import styled from 'styled-components';
import { TrevorComponent } from '@trevor/ui-components';
import {
  CATEGORY_TO_TITLE_MAP,
  CONSUMPTION_CATEGORY_TO_DISPLAY_NAME,
} from '@/features/credits/config';
import { TConsumptionCategory } from '@/features/credits/types';
import { useDatasourceId } from '@/hooks';
import { useGetCreditsConsumptionDetailsQuery } from '@/features/credits/apiSlice';
import { UUID } from '@/types';
import { AD_HOC_QUERY, DASHBOARD } from '@/features/credits/category.constants';
import {
  ActivityInfoItem,
  ActivityList,
  ActivityListHeader,
  ActivitySeeMore,
  LoadingPlaceholder,
  SeeMoreButton,
} from './ActivityInfo.styles';

export type ActivityInfoProps = {
  category: TConsumptionCategory;
  consumption: number;
  type: string;
};

const ITEMS_NUMBER_TO_SHOW_INITIAL = 3;
const ITEMS_NUMBER_TO_SHOW_INCREMENT = 5;

const ActivityInfoRaw: TrevorComponent<ActivityInfoProps> = ({
  className,
  category,
  consumption,
  type,
}) => {
  const datasourceId = useDatasourceId();
  const { data, isLoading } = useGetCreditsConsumptionDetailsQuery({
    datasourceId,
    category,
    type,
  });
  const [itemsNumberToShow, setItemsNumberToShow] = React.useState(
    ITEMS_NUMBER_TO_SHOW_INITIAL,
  );

  const showMoreItems = () =>
    setItemsNumberToShow((v) =>
      Math.min(v + ITEMS_NUMBER_TO_SHOW_INCREMENT, data!.length),
    );

  const resetItemsToShow = () =>
    setItemsNumberToShow(ITEMS_NUMBER_TO_SHOW_INITIAL);

  const ConsumerName = getNameComponent(category);

  return (
    <div className={className}>
      <ActivityList>
        <ActivityListHeader>
          <span>{CATEGORY_TO_TITLE_MAP[category] ?? 'Query'}</span>
          <span>% of {CONSUMPTION_CATEGORY_TO_DISPLAY_NAME[category]}</span>
        </ActivityListHeader>

        {isLoading && placeholderItems}

        {!isLoading && data && (
          <>
            {data
              .slice(0, itemsNumberToShow)
              .map(
                ({
                  consumerName,
                  consumedCredits,
                  consumerId,
                  datasourceId: consumerDatasourceId,
                }) => (
                  <ActivityInfoItem key={consumerId}>
                    <ConsumerName
                      consumerName={consumerName}
                      consumerId={consumerId}
                      datasourceId={consumerDatasourceId}
                    />
                    <span>
                      {Math.round((consumedCredits / consumption) * 100)}%
                    </span>
                  </ActivityInfoItem>
                ),
              )}
            {data.length > 3 && (
              <ActivitySeeMore>
                <span>
                  Displayed {itemsNumberToShow} / {data.length}
                </span>
                {itemsNumberToShow === data.length ? (
                  <SeeMoreButton onClick={resetItemsToShow}>
                    <span>Show less</span>
                  </SeeMoreButton>
                ) : (
                  <SeeMoreButton onClick={showMoreItems}>
                    <span>See more</span>
                  </SeeMoreButton>
                )}
              </ActivitySeeMore>
            )}
          </>
        )}
      </ActivityList>
    </div>
  );
};

const placeholderItems = [
  <ActivityInfoItem key="loading-placeholder=1">
    <LoadingPlaceholder $width="94px" $animationDelay={0} />
    <LoadingPlaceholder $width="140px" $animationDelay={0} />
  </ActivityInfoItem>,
  <ActivityInfoItem key="loading-placeholder=2">
    <LoadingPlaceholder $width="45px" $animationDelay={0.5} />
    <LoadingPlaceholder $width="24px" $animationDelay={0.5} />
  </ActivityInfoItem>,
  <ActivityInfoItem key="loading-placeholder=3">
    <LoadingPlaceholder $width="33px" $animationDelay={1} />
    <LoadingPlaceholder $width="24px" $animationDelay={1} />
  </ActivityInfoItem>,
] as const;

type ConsumerNameProps = {
  consumerName: string;
  // eslint-disable-next-line react/no-unused-prop-types
  datasourceId: UUID;
  // eslint-disable-next-line react/no-unused-prop-types
  consumerId?: UUID;
};

const StaticName: React.FC<ConsumerNameProps> = ({ consumerName }) => (
  <span>{consumerName}</span>
);

const SavedQueryName: React.FC<ConsumerNameProps> = ({
  consumerName,
  datasourceId,
  consumerId,
}) => (
  <a
    target="_blank"
    href={`/datasources/${datasourceId}/saved-queries/${consumerId}`}
  >
    {consumerName}
  </a>
);

const DashboardName: React.FC<ConsumerNameProps> = ({
  consumerName,
  datasourceId,
  consumerId,
}) => (
  <a
    target="_blank"
    href={`/datasources/${datasourceId}/dashboards/${consumerId}`}
  >
    {consumerName}
  </a>
);

const getNameComponent = (category: TConsumptionCategory) =>
  ((
    {
      [AD_HOC_QUERY]: StaticName,
      [DASHBOARD]: DashboardName,
    } as PRecord<TConsumptionCategory, React.FC<ConsumerNameProps>>
  )[category] ?? SavedQueryName);

export const ActivityInfo = styled(ActivityInfoRaw)`
  align-items: stretch;
  display: flex;
  border-bottom-left-radius: var(--radius-8);
  border-bottom-right-radius: var(--radius-8);
  overflow: hidden;
`;
